/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Sidebar, tituloBase, TablaFiltrable } from "../../../../shared";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import Editor from "@monaco-editor/react";
import {
  Boton,
  Titulo,
  Formulario,
  CampoInformacion,
  SubidorArchivos,
  accessAPI,
  downloadFile
} from "../../../../shared";

export default function TemplateIndividual() {
  const [template, setTemplate] = useState(null);
  const [scriptContenido, setScriptContenido] = useState("");
  const [loaderGuardarScript, setLoaderGuardarScript] = useState(false);
  const [loaderAgregarElemento, setLoaderAgregarElemento] = useState(false);
  const [elementosEnTemplate, setElementosEnTemplate] = useState([]);
  const [elementoMenuSeleccionado, setElementoMenuSeleccionado] =
    useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarEditarVisible, setSidebarEditarVisible] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [formEditarKey, setFormEditarKey] = useState(0);
  const [dialogInstrucciones, setDialogInstrucciones] = useState(false);
  const [dialogInstruccionesDocx, setDialogInstruccionesDocx] = useState(false);
  const [loaderTipo, setLoaderTipo] = useState(false);
  const [loaderDescargaDocx, setLoaderDescargaDocx] = useState(false);

  const toast = useRef(null);
  const menuElementoRef = useRef(null);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const guardarScriptRef = useRef(null);
  const navigate = useNavigate();
  const { templateId } = useParams();

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/template/detalle/${templateId}`,
      null,
      (respuesta) => {
        setTemplate(respuesta);
        setScriptContenido(respuesta.scriptContenido || "");
        setElementosEnTemplate(
          respuesta.TemplateDocumento_x_ElementoTemplate || []
        );
        document.title = respuesta.nombre;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar el template",
          sticky: true
        });
      }
    );
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function enviarScript(contenido) {
    setLoaderGuardarScript(true);
    accessAPI(
      "PUT",
      `admin/template/${template.id}`,
      { scriptContenido: contenido },
      (respuesta) => {
        setLoaderGuardarScript(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: respuesta[0]?.msg || "Script guardado",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderGuardarScript(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al guardar",
          sticky: true
        });
      }
    );
  }

  async function guardarScript() {
    if (!editorRef.current || !monacoRef.current) return;

    // Formatear el documento
    await editorRef.current.getAction("editor.action.formatDocument").run();
    const valorFormateado = editorRef.current.getModel().getValue();
    setScriptContenido(valorFormateado);

    // Validar errores de sintaxis
    const errores = monacoRef.current.editor
      .getModelMarkers({})
      .filter((m) => m.severity === monacoRef.current.MarkerSeverity.Error);
    if (errores.length > 0) {
      toast.current?.show({
        severity: "error",
        summary: "Error de sintaxis",
        detail: `Hay ${errores.length} error${errores.length > 1 ? "es" : ""} en el script. Corregir antes de guardar.`,
        life: 5000
      });
      return;
    }

    enviarScript(valorFormateado);
  }

  guardarScriptRef.current = guardarScript;

  function agregarElemento(camposFormulario) {
    setLoaderAgregarElemento(true);
    accessAPI(
      "PUT",
      `admin/template/${template.id}/elemento/${camposFormulario.elementoId}`,
      null,
      (respuesta) => {
        setLoaderAgregarElemento(false);
        setElementosEnTemplate((prev) => [...prev, respuesta]);
        setSidebarVisible(false);
      },
      (respuesta) => {
        setLoaderAgregarElemento(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar elemento",
          sticky: true
        });
      }
    );
  }

  const agregarElementoFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "elementoId",
            tipoitemformularioid: 5,
            opciones: "admin/template/elemento?dropdown=true",
            requerido: true,
            etiqueta: "Elemento",
            sinOpcionInicial: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar al template",
        callback: agregarElemento,
        validar: true
      }
    ]
  };

  function editarTemplate(camposFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PATCH",
      `admin/template/${template.id}`,
      camposFormulario,
      (respuesta) => {
        setLoaderEditar(false);
        setTemplate((prev) => ({ ...prev, nombre: camposFormulario.nombre }));
        document.title = camposFormulario.nombre;
        setSidebarEditarVisible(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: respuesta[0]?.msg || "Template actualizado",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar",
          sticky: true
        });
      }
    );
  }

  const editarFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            requerido: true,
            etiqueta: "Nombre",
            respuesta: template?.nombre
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Guardar",
        callback: editarTemplate,
        validar: true
      }
    ]
  };

  function eliminarAsignacionElemento(elementoEnTemplateId) {
    accessAPI(
      "DELETE",
      `admin/template/elemento/${elementoEnTemplateId}`,
      null,
      () => {
        setElementosEnTemplate((prev) =>
          prev.filter((e) => e.id !== elementoEnTemplateId)
        );
        toast.current?.show({
          severity: "success",
          summary: "Eliminado",
          detail: "Elemento eliminado del template",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar",
          sticky: true
        });
      }
    );
  }

  function cambiarTipo(nuevoTipo) {
    if (!template || nuevoTipo === template.tipo) return;
    setLoaderTipo(true);
    accessAPI(
      "PATCH",
      `admin/template/${template.id}`,
      { tipo: nuevoTipo },
      (respuesta) => {
        setLoaderTipo(false);
        setTemplate((prev) => ({ ...prev, tipo: nuevoTipo }));
        toast.current?.show({
          severity: "success",
          summary: "Tipo actualizado",
          detail: respuesta[0]?.msg || `Ahora usa ${nuevoTipo}`,
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderTipo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "No se pudo cambiar el tipo. Verificá que el archivo del tipo seleccionado esté cargado.",
          sticky: true
        });
      }
    );
  }

  function descargarDocx() {
    if (!template.archivodocx || loaderDescargaDocx) return;
    setLoaderDescargaDocx(true);
    downloadFile(
      `admin/template/${template.archivodocx.id}`,
      template.archivodocx.nombredescarga || "template.docx",
      () => setLoaderDescargaDocx(false),
      (respuesta) => {
        setLoaderDescargaDocx(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar",
          sticky: true
        });
      }
    );
  }

  function eliminarTemplate() {
    accessAPI(
      "DELETE",
      `admin/template/${template.id}`,
      null,
      () => {
        navigate("/template");
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar template",
          sticky: true
        });
      }
    );
  }

  const getElementoMenuItems = () => [
    {
      label: "Eliminar del template",
      icon: "pi pi-trash",
      className: "p-menuitem-danger",
      command: () => {
        if (elementoMenuSeleccionado) {
          confirmDialog({
            message: `¿Eliminar "${elementoMenuSeleccionado.elementoTemplate?.archivo?.nombredescarga}" del template?`,
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Eliminar",
            rejectLabel: "Cancelar",
            acceptClassName: "p-button-danger",
            accept: () =>
              eliminarAsignacionElemento(elementoMenuSeleccionado.id)
          });
        }
      }
    }
  ];

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={getElementoMenuItems()} popup ref={menuElementoRef} />

      {!template && (
        <div css={{ marginTop: "2em" }}>
          {/* Título: botón volver + nombre + tag + 2 botones */}
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1em",
              gap: "1em",
              flexWrap: "wrap"
            }}
          >
            <div
              css={{ display: "flex", alignItems: "center", gap: "0.75em" }}
            >
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="14rem" height="2rem" />
              <Skeleton width="6rem" height="1.4rem" />
            </div>
            <div css={{ display: "flex", gap: "0.5em" }}>
              <Skeleton width="6rem" height="2.3rem" />
              <Skeleton width="9rem" height="2.3rem" />
            </div>
          </div>

          {/* Grid 2 columnas: Detalles + Documento Word */}
          <div className="grid">
            <div className="col-12 lg:col-6">
              <Card css={{ marginBottom: "1em" }}>
                <div
                  css={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1em"
                  }}
                >
                  <Skeleton width="5rem" height="1.4rem" />
                  <Skeleton width="1.8rem" height="1.8rem" shape="circle" />
                </div>
                <Skeleton
                  width="40%"
                  height="0.8rem"
                  css={{ marginBottom: "0.4rem" }}
                />
                <Skeleton width="70%" height="1.2rem" />
              </Card>
            </div>
            <div className="col-12 lg:col-6">
              <Card css={{ marginBottom: "1em" }}>
                <div
                  css={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1em"
                  }}
                >
                  <Skeleton width="11rem" height="1.4rem" />
                  <Skeleton width="1.8rem" height="1.8rem" shape="circle" />
                </div>
                <Skeleton width="60%" height="1.2rem" />
              </Card>
            </div>
          </div>

          {/* Card Elementos a ancho completo */}
          <div className="grid">
            <div className="col-12">
              <Card css={{ marginBottom: "1em" }}>
                <div
                  css={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1em"
                  }}
                >
                  <Skeleton width="6rem" height="1.4rem" />
                  <Skeleton width="1.8rem" height="1.8rem" shape="circle" />
                </div>
                <Skeleton
                  width="100%"
                  height="2.2rem"
                  css={{ marginBottom: "0.4rem" }}
                />
                <Skeleton
                  width="100%"
                  height="2.2rem"
                  css={{ marginBottom: "0.4rem" }}
                />
                <Skeleton width="100%" height="2.2rem" />
              </Card>
            </div>
          </div>

          {/* Accordion Script colapsado */}
          <Skeleton width="100%" height="3rem" />
        </div>
      )}

      {template && (
        <div>
          <Titulo
            urlVolver="/template"
            etiquetas={[
              {
                texto: `Usando: ${
                  template.tipo === "DOCX" ? "Word" : "script"
                }`,
                error: template.tipo === "SCRIPT"
              }
            ]}
            botones={
              <>
                {((template.tipo === "DOCX" && template.archivoscript) ||
                  (template.tipo === "SCRIPT" && template.archivodocx)) && (
                  <Boton
                    transparente
                    callback={() =>
                      cambiarTipo(
                        template.tipo === "DOCX" ? "SCRIPT" : "DOCX"
                      )
                    }
                    loader={loaderTipo}
                  >
                    {template.tipo === "DOCX" ? "Usar script" : "Usar Word"}
                  </Boton>
                )}
                <Boton
                  rojo
                  confirmacion={`¿Eliminar el template "${template.nombre}"?`}
                  callback={eliminarTemplate}
                >
                  Eliminar template
                </Boton>
              </>
            }
          >
            {template.nombre}
          </Titulo>

          <div className="grid">
            <div className="col-12 lg:col-6">
              <Card css={{ marginBottom: "1em" }}>
                <Titulo
                  chico
                  botones={
                    <Boton
                      texto
                      negro
                      callback={() => {
                        setFormEditarKey((k) => k + 1);
                        setSidebarEditarVisible(true);
                      }}
                    >
                      <i className="pi pi-pencil" />
                    </Boton>
                  }
                >
                  Detalles
                </Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Nombre">
                    {template.nombre}
                  </CampoInformacion>
                </div>
              </Card>
            </div>
            <div className="col-12 lg:col-6">
              <Card css={{ marginBottom: "1em" }}>
                <Titulo
                  chico
                  etiquetas={
                    template.tipo === "DOCX"
                      ? [{ texto: "Activo", ok: true }]
                      : undefined
                  }
                  botones={
                    <SubidorArchivos
                      presignEndpoint="admin/archivo/presign"
                      confirmarEndpoint={`admin/template/${template.id}/docx/confirmar`}
                      ubicacion="templates-docx"
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      icono="pi pi-upload"
                      onSubido={(archivo) =>
                        setTemplate((prev) => ({ ...prev, archivodocx: archivo }))
                      }
                    />
                  }
                >
                  Documento Word (.docx)
                </Titulo>
                {template.archivodocx ? (
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5em"
                    }}
                  >
                    <i
                      className="pi pi-file-word"
                      css={{ color: "#2b579a" }}
                    />
                    <span>{template.archivodocx.nombredescarga}</span>
                    <Boton
                      texto
                      callback={descargarDocx}
                      loader={loaderDescargaDocx}
                    >
                      <i className="pi pi-download" />
                    </Boton>
                  </div>
                ) : (
                  <p css={{ color: "var(--grisTextos)" }}>
                    No hay archivo cargado
                  </p>
                )}
                <div css={{ marginTop: "0.75em" }}>
                  <Boton
                    texto
                    callback={() => setDialogInstruccionesDocx(true)}
                  >
                    Ver instrucciones
                  </Boton>
                </div>
              </Card>
            </div>
          </div>

          <div className="grid">
            <div className="col-12">
              <Card css={{ marginBottom: "1em" }}>
                <Titulo
                  chico
                  botones={
                    <Boton
                      icono
                      negro
                      callback={() => setSidebarVisible(true)}
                    >
                      <i className="pi pi-plus" />
                    </Boton>
                  }
                >
                  Elementos
                </Titulo>
                <TablaFiltrable conFiltros={false}
                  value={elementosEnTemplate}
                  size="small"
                  emptyMessage="No hay elementos asignados"
                  nombreArchivo="elementos_template"
                >
                  <Column
                    header="Id"
                    body={(rowData) => rowData.elementoTemplate?.id}
                    style={{ width: "80px" }}
                  />
                  <Column
                    header="Nombre"
                    body={(rowData) =>
                      rowData.elementoTemplate?.archivo?.nombredescarga
                    }
                  />
                  <Column
                    body={(rowData) => (
                      <Boton
                        icono
                        negro
                        callback={(e) => {
                          setElementoMenuSeleccionado(rowData);
                          menuElementoRef.current.toggle(e);
                        }}
                      >
                        <i className="pi pi-ellipsis-v" />
                      </Boton>
                    )}
                    style={{ width: "3rem", textAlign: "center" }}
                  />
                </TablaFiltrable>
              </Card>
            </div>
          </div>

          <Sidebar
            visible={sidebarEditarVisible}
            onHide={() => setSidebarEditarVisible(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar template</Titulo>
            <Formulario
              key={formEditarKey}
              config={editarFormConfig}
              loader={loaderEditar}
            />
          </Sidebar>

          <Sidebar
            visible={sidebarVisible}
            onHide={() => setSidebarVisible(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Agregar elemento</Titulo>
            <Formulario
              config={agregarElementoFormConfig}
              loader={loaderAgregarElemento}
            />
          </Sidebar>

          <Accordion
            activeIndex={null}
            css={{
              marginBottom: "2em",
              "& .p-accordion-header-link": { color: "black" },
              "& .p-toggleable-content": { transition: "max-height 0.15s ease" }
            }}
          >
            <AccordionTab
              header={
                <span
                  css={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.75em"
                  }}
                >
                  Script (.js)
                  {template.tipo === "SCRIPT" && (
                    <Tag value="ACTIVO" severity="success" />
                  )}
                </span>
              }
            >
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5em"
                }}
              >
                <Boton texto callback={() => setDialogInstrucciones(true)}>
                  Ver instrucciones
                </Boton>
                <Boton
                  verde
                  callback={guardarScript}
                  loader={loaderGuardarScript}
                >
                  <i className="pi pi-save" css={{ marginRight: "0.5em" }} />
                  Guardar script
                </Boton>
              </div>
              <div
                css={{
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  overflow: "hidden"
                }}
              >
                <Editor
                  height="500px"
                  defaultLanguage="javascript"
                  value={scriptContenido}
                  onChange={(value) => setScriptContenido(value || "")}
                  onMount={(editor, monaco) => {
                    editorRef.current = editor;
                    monacoRef.current = monaco;
                    editor.addCommand(
                      monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                      () => guardarScriptRef.current()
                    );
                  }}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    scrollBeyondLastLine: false,
                    automaticLayout: true
                  }}
                />
              </div>
            </AccordionTab>
          </Accordion>

          <Dialog
            header="Cómo armar un template script (JS)"
            visible={dialogInstrucciones}
            onHide={() => setDialogInstrucciones(false)}
            style={{ width: "60rem" }}
            maximizable
            css={{
              "& code": {
                fontFamily: "monospace",
                backgroundColor: "#f4f4f4",
                padding: "0 0.3em",
                borderRadius: "3px",
                fontSize: "0.9em"
              },
              "& pre": {
                fontFamily: "monospace",
                backgroundColor: "#f4f4f4",
                padding: "0.75em",
                borderRadius: "4px",
                fontSize: "0.85em",
                overflowX: "auto",
                margin: "0.5em 0",
                whiteSpace: "pre-wrap"
              },
              "& h4": {
                marginTop: "1.5em",
                marginBottom: "0.5em",
                color: "var(--principal)"
              },
              "& h4:first-of-type": { marginTop: 0 },
              "& h5": {
                marginTop: "1em",
                marginBottom: "0.4em",
                color: "var(--negro)"
              },
              "& dl": { margin: "0.5em 0" },
              "& dt": {
                fontFamily: "monospace",
                fontWeight: 600,
                marginTop: "0.6em"
              },
              "& dd": {
                marginLeft: "1.5em",
                marginTop: "0.2em",
                fontSize: "0.95em",
                color: "#444"
              }
            }}
          >
            <h4>Cómo funciona</h4>
            <p>
              Un template script es un fragmento de <b>JavaScript</b> que el
              sistema ejecuta para generar un PDF. El script tiene acceso a un
              objeto <code>datos</code> con la información del recurso
              (proceso, certificado, propuesta, etc.) y a un conjunto de
              funciones para dibujar sobre el documento.
            </p>
            <p>
              El script debe arrancar siempre con{" "}
              <code>inicializarDocumento(...)</code>. El guardado del PDF
              ocurre automáticamente al terminar.
            </p>

            <h5>Estructura mínima</h5>
            <pre>{`inicializarDocumento("portrait", 210, 297);

agregarTexto("Hola", "OpenSans-Bold", 14, "black", 20, 30);
// El script recibe los datos CRUDOS: el cliente vive dentro del recurso.
agregarTexto(\`Cliente: \${datos.actividad.proceso.cliente.razonsocial}\`,
  "OpenSans-Regular", 11, "black", 20, 40);`}</pre>

            <h4>Cómo corre el script (y qué no está disponible)</h4>
            <p>
              El script corre aislado: sin red, sin archivos, sin variables de
              entorno y con un tope de tiempo. Además hay dos cosas que no
              están, y conviene saber por qué:
            </p>
            <dl>
              <dt>Es sincrónico: no hay <code>async</code> ni <code>await</code></dt>
              <dd>
                Un <code>await</code> deja el tope de tiempo sin efecto, así que
                no se admite. Escribir <code>await</code> da un error de
                sintaxis, y el mensaje de V8 no ayuda: dice{" "}
                <code>missing ) after argument list</code>. Si ves eso, buscá un{" "}
                <code>await</code>.
              </dd>
              <dt>No hay <code>Date</code>: usá <code>fechaActual</code></dt>
              <dd>
                La fecha del momento llega ya resuelta como{" "}
                <code>fechaActual.anio</code>, <code>.mes</code>,{" "}
                <code>.dia</code> e <code>.iso</code>. Es aparte de las fechas
                de negocio que vengan en <code>datos</code> (emisión,
                vencimiento), que son otra cosa.
              </dd>
              <dt>No hay <code>Math.random</code></dt>
              <dd>
                El template se ejecuta dos veces cuando usa{" "}
                <code>obtenerAncho</code> —una para medir y otra para dibujar—
                y las dos tienen que pedir exactamente lo mismo. El resto de{" "}
                <code>Math</code> está disponible.
              </dd>
              <dt>Sí hay <code>console.log</code>, para debuggear</dt>
              <dd>
                <code>console.log(...)</code> (también <code>.warn</code> y{" "}
                <code>.error</code>) no interrumpe la generación: lo logueado
                sale en los logs del servidor, prefijado con el número del
                template. No aparece en el PDF — para ver un valor en el
                documento mientras probás, dibujalo con{" "}
                <code>agregarTexto</code>. Tope: 200 líneas por ejecución.
              </dd>
            </dl>

            <h4>Funciones de dibujo</h4>
            <dl>
              <dt>
                inicializarDocumento(orientation, ancho, alto)
              </dt>
              <dd>
                Crea el documento. <code>orientation</code> es{" "}
                <code>"portrait"</code> o <code>"landscape"</code>; ancho y
                alto en milímetros (ej: A4 → 210, 297).{" "}
                <b>Debe ser la primera llamada del script.</b>
              </dd>
              <dt>agregarPagina(ancho=null, alto=null)</dt>
              <dd>
                Agrega una página más al documento. Si no se pasan medidas,
                usa las del init.
              </dd>
              <dt>
                agregarTexto(texto, fuente, tamaño, color, x, y, props?, anchoMaximo?)
              </dt>
              <dd>
                Dibuja texto. <code>fuente</code> es uno de{" "}
                <code>"OpenSans-Regular"</code>,{" "}
                <code>"OpenSans-Bold"</code>,{" "}
                <code>"OpenSans-Light"</code>,{" "}
                <code>"OpenSans-ExtraBold"</code>,{" "}
                <code>"Roboto-Regular"</code>, <code>"Roboto-Bold"</code>.
                <code>props</code> opcional: <code>{`{ align, angle, maxWidth, charSpace, lineHeightFactor }`}</code>.
                <br />
                <code>anchoMaximo</code> (en mm, opcional) es cuánto puede
                ocupar el texto: si no entra, la plataforma achica la fuente al
                dibujar, hasta un piso de 4pt. Va después de{" "}
                <code>props</code>, pero si no usás props podés pasarlo directo
                en su lugar:{" "}
                <code>{`agregarTexto(txt, "OpenSans-Bold", 12, "black", 20, 40, 60)`}</code>.
                <br />
                Ojo: es el espacio que puede ocupar el <i>texto</i>, que arranca
                en <code>x</code>. Para que no se pase del borde, declarar lo
                disponible desde ahí (<code>ancho - x</code>), no el ancho total
                del documento. Centrado con <code>align</code>, el texto se
                reparte para los dos lados de <code>x</code>.
                <br />
                Es la forma recomendada de ajustar un texto a una caja, y no{" "}
                <code>obtenerAncho</code>: como el script no decide nada, el
                template se resuelve en una sola pasada.
              </dd>
              <dt>
                agregarImagen(imagen, x, y, ancho, alto, props?)
              </dt>
              <dd>
                Dibuja una imagen. <code>imagen</code> puede ser{" "}
                <code>elementosTemplate[id]</code> o el resultado de{" "}
                <code>utilitarios.generarQR(url)</code>, <b>sin</b>{" "}
                <code>await</code>.
              </dd>
              <dt>dibujarLinea(color, x1, y1, x2, y2)</dt>
              <dd>Línea entre dos puntos.</dd>
              <dt>
                dibujarElipse(colorLinea, colorRelleno, x, y, radioX, radioY, anchoLinea, estilo)
              </dt>
              <dd>
                Elipse / círculo. <code>estilo</code>: <code>"S"</code> solo
                contorno, <code>"F"</code> solo relleno,{" "}
                <code>"FD"</code> ambos.
              </dd>
              <dt>
                dibujarRectangulo(colorLinea, colorRelleno, x, y, ancho, alto, anchoLinea, estilo)
              </dt>
              <dd>Rectángulo regular. Mismos modos de estilo.</dd>
              <dt>
                dibujarRectanguloRedondo(colorLinea, colorRelleno, x, y, ancho, alto, radioX, radioY, anchoLinea, estilo)
              </dt>
              <dd>Rectángulo con esquinas redondeadas.</dd>
              <dt>obtenerAncho(texto, fuente, tamaño)</dt>
              <dd>
                Devuelve el ancho en mm de un texto con la fuente y tamaño
                dados. Sirve para posicionar algo al lado de un texto, o para
                centrarlo.
                <br />
                <b>No lo uses para achicar hasta que entre.</b> Un{" "}
                <code>{`while (obtenerAncho(...) > X) tam--`}</code> pide
                mediciones distintas según lo que ya midió, y eso hace que el
                template se rechace: lo que se dibujaría no sería lo que se
                midió. Para ajustar a una caja está{" "}
                <code>anchoMaximo</code> en <code>agregarTexto</code>.
                <br />
                <b>Cuidado:</b> si le pasás <code>undefined</code> el
                template falla — siempre garantizá un string
                (<code>{`|| ""`}</code>).
              </dd>
            </dl>

            <h4>Utilitarios (objeto <code>utilitarios</code>)</h4>
            <dl>
              <dt>utilitarios.formatFecha(fecha)</dt>
              <dd>
                Devuelve la fecha en formato <code>YYYY-MM-DD</code>: toma el
                comienzo del string ISO. Para mostrarla como{" "}
                <code>DD/MM/YYYY</code>, partila:{" "}
                <code>{`utilitarios.formatFecha(f).split("-").reverse().join("/")`}</code>.
              </dd>
              <dt>
                utilitarios.formatearNumero(n, decimales, digitosTotales)
              </dt>
              <dd>
                Redondea a <code>decimales</code>, usa coma decimal y completa
                con ceros a la izquierda. El relleno se calcula con el largo del
                número original, no del formateado.
              </dd>
              <dt>utilitarios.generarQR(url)</dt>
              <dd>
                Genera un QR a partir de una URL. Va <b>sin</b>{" "}
                <code>await</code> —el script es sincrónico y con{" "}
                <code>await</code> ni siquiera compila—:{" "}
                <code>{`agregarImagen(utilitarios.generarQR(url), x, y, ancho, alto)`}</code>.
                <br />
                Para el link público de un certificado, armá la URL con su
                número y encodealo:{" "}
                <code>{`utilitarios.generarQR(\`https://TU-DOMINIO/c/\${encodeURIComponent(numero)}\`)`}</code>.
                Dónde está el número depende del template:{" "}
                <code>datos.numeroCertificado</code> en el de Certificado,{" "}
                <code>datos.certificado.numero</code> en Marca y en Etiqueta de
                Datos.{" "}
                Evitá armarlo como{" "}
                <code>{`/c/{letra}/{numeroDeProceso}`}</code>: esa forma existe
                solo por compatibilidad con los QR ya impresos y deja de
                resolver en cuanto el organismo usa numeración propia.
              </dd>
            </dl>

            <h4>Recursos extras</h4>
            <dl>
              <dt>elementosTemplate[id]</dt>
              <dd>
                Imágenes asociadas al template (fondos, logos, firmas). El{" "}
                <code>id</code> es el de <i>ElementoTemplate</i> en la base.
                Se usa como primer argumento de <code>agregarImagen</code>.
                Si un elemento no está disponible en S3, queda fuera de este
                objeto y referenciarlo va a dar <code>undefined</code> —
                revisar la consola.
              </dd>
              <dt>monedas</dt>
              <dd>
                Array con todas las monedas del sistema. Cada moneda tiene{" "}
                <code>id</code>, <code>nombre</code>, <code>simbolo</code>,{" "}
                <code>codigo</code>.
              </dd>
            </dl>

            <h4>Acreditaciones y marcas</h4>
            <p>
              Cuando el documento tiene norma (con su <code>id</code>), el
              sistema le agrega las acreditaciones vigentes. Una acreditación
              suspendida no viaja, igual que si la norma nunca hubiera estado
              acreditada:
            </p>
            <pre>{`norma: {
  ...,
  acreditacion: { id, nombre, pais, linkAlcance, marca } | null,  // la primera vigente
  acreditaciones: [{ id, nombre, pais, linkAlcance, marca }]      // todas las vigentes
}`}</pre>
            <p>
              <code>marca</code> es la imagen de la marca de la acreditación,
              lista para <code>agregarImagen</code>. Es <code>null</code> si la
              acreditación no tiene marca cargada:
            </p>
            <pre>{`const acreditacion = datos.actividad.proceso.norma?.acreditacion;
if (acreditacion?.marca) {
  agregarImagen(acreditacion.marca, 170, 20, 25, 25);
}`}</pre>
            <p>
              Tienen norma con acreditaciones: Certificado, Marca de
              Conformidad, Documento de actividad, Etiqueta de Producto (solo
              al emitir) y Propuesta Comercial (en{" "}
              <code>datos.certificado.normas</code>). La Etiqueta de Datos
              trae el número de la norma pero no sus acreditaciones.
            </p>

            <h4>Buenas prácticas</h4>
            <ul>
              <li>
                <b>Sitios pueden ser <code>null</code></b>. Protegete con{" "}
                <code>?.</code> y fallbacks:{" "}
                <code>{`datos.sitioFabricante?.nombre || "—"`}</code>. Si la
                sección entera no aplica cuando es null, envolvela en{" "}
                <code>if (datos.sitioFabricante) {`{ ... }`}</code>.
              </li>
              <li>
                <b>
                  No pases <code>undefined</code> a{" "}
                  <code>obtenerAncho</code>
                </b>{" "}
                — el template falla. Asigná a una variable con{" "}
                <code>{`|| ""`}</code> antes de medir.
              </li>
              <li>
                <b>El script corre dentro de un <code>try/catch</code>:</b>{" "}
                si tira un error, el backend devuelve un 500 con mensaje y
                número de línea relativa al template (no al archivo
                ejecutado).
              </li>
              <li>
                <b>Atajos de teclado del editor:</b> <code>Ctrl/Cmd+S</code>{" "}
                guarda el script.
              </li>
            </ul>

            <h4>Estructura de datos por tipo</h4>
            <p>
              El objeto <code>datos</code> que recibe el script es distinto
              según el tipo de documento. Los templates que salen de un proceso
              (certificado, marca, etiquetas de datos y de producto, documento
              de actividad, propuesta) reciben además los sitios en el
              top-level: <code>datos.sitioPrincipal</code>,{" "}
              <code>datos.sitioFabrica</code>,{" "}
              <code>datos.sitioFabricante</code> (pueden ser <code>null</code>)
              y, salvo la propuesta, <code>datos.sitiosAdicionales</code>. Las
              etiquetas de ensayo, muestra y caja y el certificado de familias
              de juguetes no reciben sitios.
            </p>
            <p
              css={{
                background: "var(--amarilloLight)",
                borderLeft: "3px solid var(--amarillo)",
                padding: "0.6em 0.8em",
                borderRadius: "4px"
              }}
            >
              <b>Importante — el script recibe los datos CRUDOS</b> (sin
              enriquecer). A diferencia de los templates Word (.docx):
              <ul css={{ margin: "0.4em 0 0", paddingLeft: "1.2em" }}>
                <li>
                  El <b>cliente</b> vive dentro del recurso, no en el
                  top-level: <code>datos.actividad.proceso.cliente</code> en
                  el certificado y el documento de actividad,{" "}
                  <code>datos.certificado.proceso.cliente</code> en la marca y
                  la etiqueta de datos, <code>datos.proceso.cliente</code> en la
                  etiqueta de producto y <code>datos.cliente</code> en la
                  propuesta.
                </li>
                <li>
                  Las <b>fechas</b> llegan como <b>string ISO</b> (ej.{" "}
                  <code>"2026-05-20T00:00:00.000Z"</code>), no como objetos{" "}
                  <code>Date</code>: los datos entran al script como JSON.
                  Formatealas con <code>utilitarios.formatFecha(...)</code>.
                </li>
                <li>
                  Los <b>sitios</b> son objetos crudos, sin helpers como{" "}
                  <code>.completa</code> o <code>.lineaCalle</code>:
                  <ul css={{ paddingLeft: "1.2em" }}>
                    <li>
                      <code>sitioPrincipal</code> (sede del cliente):{" "}
                      <code>direccion</code>, <code>direccion2</code>,{" "}
                      <code>codigoPostal</code>, <code>ciudad</code>,{" "}
                      <code>estado</code>, <code>pais</code>,{" "}
                      <code>headquarters</code>,{" "}
                      <code>camposAdicionales</code>. En los flujos de
                      certificados no trae <code>nombre</code> ni{" "}
                      <code>telefono</code>.
                    </li>
                    <li>
                      <code>sitioFabrica</code>, <code>sitioFabricante</code>{" "}
                      y <code>sitiosAdicionales[]</code>: <code>id</code>,{" "}
                      <code>nombre</code>, <code>direccion</code>,{" "}
                      <code>direccion2</code>, <code>codigoPostal</code>,{" "}
                      <code>ciudad</code>, <code>estado</code>,{" "}
                      <code>pais</code>, <code>telefono</code>,{" "}
                      <code>camposAdicionales</code> y, según el flujo,{" "}
                      <code>sitioTipo</code>.
                    </li>
                    <li>
                      <code>camposAdicionales</code> es{" "}
                      <code>{`[{ nombreCampo, valor }]`}</code>.
                    </li>
                  </ul>
                </li>
                <li>
                  Los reportes de ensayo y los productos llegan crudos en{" "}
                  <code>datos.actividad.reporteensayo</code> y{" "}
                  <code>datos.actividad.proceso.producto</code> (no como{" "}
                  <code>reportesEnsayo</code>/<code>producto</code> enriquecidos).
                </li>
              </ul>
              <span css={{ display: "block", marginTop: "0.4em" }}>
                Ante la duda, hacé{" "}
                <code>console.log(datos)</code> al inicio del script: la forma
                exacta espeja el <i>select</i> de Prisma del recurso.
              </span>
            </p>

            <h5>Certificado</h5>
            <pre>{`// Sale al emitir, re-emitir, regenerar y en extensiones.
datos = {
  // Recurso principal: la ACTIVIDAD (con el proceso adentro). El cliente del
  // certificado es datos.actividad.proceso.cliente. En una EXTENSIÓN, acá
  // llegan los datos de la empresa extendida (mismo lugar, distinto cliente).
  actividad: {
    fechafinalizacion,               // string ISO
    actividadtipo: { correspondeemision, nombre },
    reporteensayo: [{
      id, codigo, fechaemision,
      laboratorio: { razonsocial, direccion, codigopostal, ciudad, estado, pais }
    }],
    proceso: {
      id, nombre, nombrefantasia, juguetespagodni,
      cliente: {
        id, razonsocial, nombrefantasia,
        sitios: [{ ... }],           // solo la sede principal (máximo 1)
        empleadoclientes: [{ nombre, apellido, email }]  // responsables de certificación
      },
      certificadotipo: {
        codigo, nombre, reglamentacion, textolegalcertificado,
        duracioncertificado, nombreursea, gestionadoursea,
        procesotipo: { letra }
      },
      norma: {
        id, nombrecertificado, numero, acreditadaBool,
        acreditacion: { id, nombre, pais, linkAlcance, marca } | null,
        acreditaciones: [{ id, nombre, pais, linkAlcance, marca }]
      } | null,
      esquema: { nombre, etiqueta },
      // Solo productos vigentes; campos propios ordenados:
      producto: [{ id, producto_x_productocampospropios: [{ valor, productocampospropios: { nombre, etiqueta, enviaraurseabool } }] }],
      sitioFabrica: { ... } | null,
      sitioFabricante: { ... } | null,
      sitiosAdicionales: [{ ... }],  // sitios COMPLETOS, no ids
      certificado: [...]             // certificados previos del proceso (vacío en la 1ª emisión)
    }
  },
  // OJO: acá NO hay datos.certificado. El número llega aparte, ya congelado.
  // En una extensión es el del certificado original (sin el "/N").
  numeroCertificado: string,         // ej "CP-J-1002". Para el QR y para imprimirlo.
  fechaEmision,                      // string ISO: utilitarios.formatFecha(datos.fechaEmision)
  fechaVencimiento,
  // Solo en extensiones: número correlativo de la extensión dentro del
  // certificado. Para armar el código:
  //   const cod = datos.numeroCertificado +
  //     (datos.extension?.numero ? \`/\${datos.extension.numero}\` : "");
  extension: { numero: Int } | undefined,
  clienteOverride: { razonsocial, nombrefantasia } | undefined,  // solo extensiones
  // Sitios CRUDOS en el top-level. sitioPrincipal es la sede principal del
  // cliente (o del cliente extendido en una extensión).
  sitioPrincipal: { ... } | null,
  sitioFabrica: { ... } | null,
  sitioFabricante: { ... } | null,
  sitiosAdicionales: [{ ... }]
}`}</pre>

            <h5>Marca de Conformidad</h5>
            <pre>{`datos = {
  // Recurso principal: el CERTIFICADO (con el proceso adentro). El cliente es
  // datos.certificado.proceso.cliente (siempre el titular).
  certificado: {
    id,
    numero,                  // ej "CP-J-1002". Para el QR.
    fechaemision, fechavencimiento,
    proceso: {
      id, nombre, nombrefantasia,
      cliente: { razonsocial, nombrefantasia, sitios: [{ ... }] },
      certificadotipo: { codigo, nombre, reglamentacion, nombreursea, procesotipo: { letra } },
      norma: {
        id, nombrecertificado, numero, acreditadaBool,
        acreditacion: { id, nombre, pais, linkAlcance, marca } | null,
        acreditaciones: [{ id, nombre, pais, linkAlcance, marca }]
      } | null,
      sitioFabrica: { ... } | null,
      sitioFabricante: { ... } | null
    }
  },
  // Solo desde el portal del cliente, si el certificado es una extensión:
  clienteOverride: { razonsocial, nombrefantasia } | undefined,
  fechaEmision,              // string ISO
  fechaVencimiento,
  sitioPrincipal: { ... } | null,   // sede del cliente extendido si es extensión
  sitioFabrica: { ... } | null,
  sitioFabricante: { ... } | null,
  sitiosAdicionales: [{ ... }]
}`}</pre>

            <h5>Etiqueta de Datos del Cliente</h5>
            <pre>{`datos = {
  // Recurso principal: el CERTIFICADO. El cliente es
  // datos.certificado.proceso.cliente.
  certificado: {
    id,
    numero,                  // ej "CP-J-1002". Para el QR.
    proceso: {
      id, nombre,
      norma: { numero } | null,     // sin acreditaciones
      cliente: { razonsocial, sitios: [{ ... }] },
      certificadotipo: { codigo, templateEtiquetaDetallesId, procesotipo: { letra } },
      sitioFabrica: { ... } | null,
      sitioFabricante: { ... } | null
    }
  },
  // Solo desde el portal del cliente, si el certificado es una extensión:
  clienteOverride: { razonsocial } | undefined,
  sitioPrincipal: { ... } | null,
  sitioFabrica: { ... } | null,
  sitioFabricante: { ... } | null,
  sitiosAdicionales: [{ ... }]
}`}</pre>

            <h5>Etiqueta de Producto</h5>
            <pre>{`// Sale al emitir, desde "Generar etiquetas" del admin y en extensiones.
// Los datos son los mismos en los tres casos; solo cambia lo marcado como
// "solo en extensiones".
datos = {
  // Recurso principal: el PROCESO completo (directo, no dentro de certificado).
  // El cliente es datos.proceso.cliente: siempre el TITULAR, también en extensiones.
  proceso: {
    id, nombre, nombrefantasia, juguetespagodni,
    cliente: {
      id, razonsocial, nombrefantasia,
      sitios: [{ ... }],           // solo la sede principal (máximo 1)
      empleadoclientes: [{ nombre, apellido, email }]  // responsables de certificación
    },
    certificadotipo: {
      codigo, nombre, reglamentacion, textolegalcertificado,
      duracioncertificado, nombreursea, gestionadoursea,
      procesotipo: { letra }
    },
    norma: {
      id, nombrecertificado, numero, acreditadaBool,
      acreditacion: { id, nombre, pais, linkAlcance, marca } | null,
      acreditaciones: [{ id, nombre, pais, linkAlcance, marca }]
    } | null,
    esquema: { nombre, etiqueta },
    // Solo productos vigentes; campos propios ordenados:
    producto: [{ id, producto_x_productocampospropios: [{ valor, productocampospropios: { nombre, etiqueta, enviaraurseabool } }] }],
    sitioFabrica: { ... } | null,
    sitioFabricante: { ... } | null,
    sitiosAdicionales: [{ ... }],  // sitios COMPLETOS, no ids
    // El certificado vigente del proceso (ya emitido, también al emitir):
    certificado: [{
      id, numero, fechavencimiento,
      estadocertificado: { id, vigentebool, bajadefinitiva },
      emisioncertificado: [{ id, fechaemision }],
      extensioncertificado: [{ clienteextendido: { razonsocial } }]
    }]
  },
  // Razón social a imprimir: la del cliente extendido si es una extensión,
  // sino la del cliente del proceso. Conviene usar este campo directamente.
  razonSocialCliente: string,
  extension: { numero: Int } | undefined,   // solo en extensiones
  // El producto de esta etiqueta (uno de datos.proceso.producto):
  producto: {
    id,
    producto_x_productocampospropios: [{
      valor,
      productocampospropios: { nombre, etiqueta, enviaraurseabool }
    }]
  },
  // sitioPrincipal es la sede del cliente extendido en una extensión.
  sitioPrincipal: { ... } | null,
  sitioFabrica: { ... } | null,
  sitioFabricante: { ... } | null,
  sitiosAdicionales: [{ ... }]
}`}</pre>

            <h5>Etiqueta de Ensayo</h5>
            <pre>{`// Se dibuja UNA etiqueta: inicializarDocumento declara el tamaño de la
// etiqueta, no el de una hoja. El mismo script sirve para la etiqueta suelta
// y para el lote, donde el sistema repite la etiqueta en grilla sobre la hoja.
datos = {
  numeroEnsayo: Int,         // el correlativo del ensayo, ej 1000
  numeroMuestra: Int | null, // null si el ensayo todavía no tiene muestra (en el lote, los pendientes)
  // La clave que el laboratorio tipea a mano en la página pública. Se imprime
  // en la etiqueta pero NUNCA se codifica en el QR: el QR lleva solo la URL.
  claveSeguridad: string,
  notasEtiqueta: string | null,
  // La URL pública de verificación del ensayo. El QR se genera acá adentro,
  // en el template — no llega pre-generado:
  //   agregarImagen(utilitarios.generarQR(datos.urlVerificacion), x, y, ancho, alto);
  urlVerificacion: string
}`}</pre>

            <h5>Etiqueta de Muestra y Etiqueta de Caja</h5>
            <pre>{`// Una etiqueta por ejecución, suelta o repetida en grilla (lote).
// Solo admiten template script.
datos = {
  id,
  numero: Int,
  // Para el QR público: <portal>/m/<hash> (muestra) o <portal>/caja/<hash>
  // (caja). No llega la URL armada: el dominio va escrito en el template.
  hash: string
}`}</pre>

            <h5>Certificado de familias de juguetes</h5>
            <pre>{`// Solo admite template script.
datos = {
  certificadoNumero: Int,
  cliente: string,           // razón social
  clienteDireccion: string,  // "direccion, ciudad, pais" de la sede principal, o ""
  factura: string,           // lo que se escribió en el formulario
  fechaEmision: string,      // ya formateada: "YYYY-MM-DD"
  juguetes: [{ codigo, descripcion }]   // el primero es el padre
}`}</pre>

            <h5>Documento de actividad (al finalizar)</h5>
            <pre>{`// Recurso principal: la ACTIVIDAD completa, con pasos y colaboradores.
datos = {
  actividad: {
    id,
    fechacreacion, fechafinalizacion,   // strings ISO
    archivos,                // [{ id, nombre, fecha, disponibleParaClientes }] | null (antes de agregar este documento)
    actividadtipoid,
    actividadtipo: { nombre },
    // Pasos de la actividad (sin ordenar: ordenar por orden)
    actividadpaso: [{
      id, fechaInicioPaso,
      nombre, orden,
      actividadpasoconfig: { nombre, orden },   // igual que nombre y orden; se mantiene por compatibilidad
      // Colaboradores asignados al paso, con sus decisiones
      actividadpaso_x_auditor: [{
        activo, decisor, enformacion,
        auditor: { id, nombre, apellido, email },
        actividadpasodecision: [{ comentarios, fechadecision, aprobado }]
      }],
      // Decisiones del admin sobre el paso (si las hubo)
      actividadpasodecisionadmin: [{ comentarios, fechadecision, aprobado }]
    }],
    reporteensayo: [{
      id, codigo, fechaemision,
      laboratorio: { razonsocial, direccion, codigopostal, ciudad, estado, pais }
    }],
    proceso: {
      id, nombre, nombrefantasia, certificadotipoid,
      sitiosAdicionalesIds: [Int],   // solo ids: los sitios completos están en datos.sitiosAdicionales
      cliente: { id, razonsocial, nombrefantasia, sitios: [{ ... }] },  // solo la sede principal
      certificadotipo: { codigo, nombre, reglamentacion, textolegalcertificado,
                         duracioncertificado, nombreursea, gestionadoursea,
                         procesotipo: { letra } },
      norma: {
        id, nombrecertificado, numero, acreditadaBool,
        acreditacion: { id, nombre, pais, linkAlcance, marca } | null,
        acreditaciones: [{ id, nombre, pais, linkAlcance, marca }]
      } | null,
      esquema: { nombre, etiqueta } | null,
      // Solo productos vigentes; campos propios ordenados:
      producto: [{ id, producto_x_productocampospropios: [{ valor, productocampospropios: { nombre, etiqueta, enviaraurseabool } }] }],
      certificado: [{ numero }],     // certificado vigente del proceso (máximo 1)
      sitioFabrica: { ... } | null,
      sitioFabricante: { ... } | null
    }
  },
  fechaGeneracion,           // momento en que se genera el documento
  clienteContactos: string,  // contactos del cliente separados por coma
  sitioPrincipal: { ... } | null,
  sitioFabrica: { ... } | null,
  sitioFabricante: { ... } | null,
  sitiosAdicionales: [{ ... }]
  // NO vienen: fechaEmision, fechaVencimiento, extension, numeroCertificado
}`}</pre>

            <h5>Propuesta Comercial</h5>
            <pre>{`// datos ES la propuesta misma (no está envuelta)
datos = {
  id, clienteid, usuarioemisorid,
  nombreAceptador,           // texto "{nombre} {apellido}" | null
  archivoId,                 // archivo adjunto (propuestas retroactivas) | null
  solicitudProcesoId,        // null en propuestas históricas
  procesoIds: [Int],         // procesos elegidos al generar la propuesta
  fechacreacion, fechaemision, fechaaceptacion,   // strings ISO
  notas, notasInternas,
  masiva: Boolean,           // true = "más IVA", precios sin IVA
  archivada: Boolean, notasArchivada,
  precios: {
    // Una entrada por actividad del tipo de certificado (precio acordado).
    actividades: [{
      precioActividadClienteId,
      actividadTipoId, certificadoTipoId,
      actividadnombre,                 // ej. "Certificación"
      certificadonombre,               // nombre del tipo de certificado
      procesotiponombre,               // nombre del tipo de proceso
      certificadoNotasPropuesta,       // notas del tipo de certificado (para la propuesta)
      precio, monedaid,                // precio unitario (null = sin definir, 0 = bonificada)
      moneda: { id, nombre, simbolo } | null,
      frecuenciaRefacturacion,         // "SEMANAL" | "MENSUAL" | "SEMESTRAL" | "ANUAL" | "BIENAL" | "TRIENAL" | null (facturación única)
      crearAutomaticamente: Boolean,   // se instancia y factura al aceptar
      cantidad, subtotal,              // primera facturación: procesos × precio (0 si no se crea automáticamente)
      // Inyectado por el endpoint: descuento aplicable según el cliente
      descuento: { porcentajeDescuento, concepto } | null
    }],
    // Uno por tipo de certificado de las actividades (mismos campos que
    // datos.certificado, abajo).
    certificados: [{ ... }],
    // Ítems sueltos (no referencian proceso).
    adicionales: [{ id, descripcion, precio, monedaid, moneda: { id, nombre, simbolo } | null, modifica: Boolean }],
    totales: [{ total, moneda: { id, nombre, simbolo } }]
  },
  // El certificado de la propuesta: lo que es del tipo de certificado y no
  // de cada actividad. Si la propuesta cubre más de un tipo, es el primero
  // (todos están en precios.certificados).
  certificado: {
    certificadoTipoId,
    nombre,                  // nombre del tipo de certificado
    procesotiponombre,       // nombre del tipo de proceso
    notasPropuesta,          // notas del tipo de certificado (para la propuesta)
    reglamentacion,          // reglamentación del tipo de certificado
    // Normas y esquemas (sin repetir) de los procesos de la propuesta con
    // ese tipo de certificado. Normalmente uno de cada uno:
    normas: [{
      id, numero, nombre,
      acreditacion: { id, nombre, pais, linkAlcance, marca } | null,
      acreditaciones: [{ id, nombre, pais, linkAlcance, marca }]
    }],
    esquemas: [{ id, etiqueta, descripcion, detalles }]
  } | null,
  // Propuestas viejas sin precios en el modelo nuevo: precios es el JSON que
  // se guardó en su momento (o null), y certificado es null.
  cliente: {
    id, razonsocial, nombrefantasia, rut, telefono, ...,   // todos los campos del cliente
    descuentos: [{
      id, porcentajeDescuento, concepto,
      certificadoTipo: { id, nombre, procesotipo: { nombre } }
    }],
    sitios: [{ ... }]        // solo la sede principal
  },
  usuarioemisor: { nombre, apellido, email },
  solicitud: {
    id, nombrefantasia, fecha, ...,  // todos los campos de la solicitud
    sitioFabrica: { id, nombre, ..., camposAdicionales, sitioTipo: { id, nombre, ... } } | null,
    sitioFabricante: { id, nombre, ..., camposAdicionales, sitioTipo: { id, nombre, ... } } | null
  } | null,
  // Sitios CRUDOS al top-level (los mismos objetos de arriba):
  sitioPrincipal: { ... } | null,   // = cliente.sitios[0]
  sitioFabrica: { ... } | null,     // = solicitud.sitioFabrica
  sitioFabricante: { ... } | null,
  // Procesos alcanzados por la propuesta.
  procesos: [{ id, nombre, nombrefantasia }],
  // Todas las normas de precios.certificados, sin repetir (los mismos objetos):
  normas: [{ id, numero, nombre, acreditacion, acreditaciones }]
}`}</pre>
          </Dialog>

          <Dialog
            header="Cómo armar un template Word (.docx)"
            visible={dialogInstruccionesDocx}
            onHide={() => setDialogInstruccionesDocx(false)}
            style={{ width: "60rem" }}
            maximizable
            css={{
              "& code": {
                fontFamily: "monospace",
                backgroundColor: "#f4f4f4",
                padding: "0 0.3em",
                borderRadius: "3px",
                fontSize: "0.9em"
              },
              "& pre": {
                fontFamily: "monospace",
                backgroundColor: "#f4f4f4",
                padding: "0.75em",
                borderRadius: "4px",
                fontSize: "0.85em",
                overflowX: "auto",
                margin: "0.5em 0",
                whiteSpace: "pre-wrap"
              },
              "& h4": {
                marginTop: "1.5em",
                marginBottom: "0.5em",
                color: "var(--principal)"
              },
              "& h4:first-of-type": { marginTop: 0 },
              "& h5": {
                marginTop: "1em",
                marginBottom: "0.4em",
                color: "var(--negro)"
              },
              "& dl": { margin: "0.5em 0" },
              "& dt": {
                fontFamily: "monospace",
                fontWeight: 600,
                marginTop: "0.6em"
              },
              "& dd": {
                marginLeft: "1.5em",
                marginTop: "0.2em",
                fontSize: "0.95em",
                color: "#444"
              }
            }}
          >
            <h4>Cómo funciona</h4>
            <p>
              El sistema descarga el documento Word, reemplaza los tags{" "}
              <code>[[xxx]]</code> por los valores del recurso (proceso,
              certificado, actividad, propuesta...) y convierte el resultado a
              PDF con LibreOffice. Los tags no resueltos se eliminan (no quedan{" "}
              <code>[[xxx]]</code> en el PDF).
            </p>

            <h4>Sintaxis básica</h4>
            <p>
              Los tags usan corchetes dobles y soportan acceso a propiedades
              anidadas con punto:
            </p>
            <pre>{`[[cliente.razonsocial]]
[[proceso.nombre]]
[[proceso.sitioFabrica.completa]]`}</pre>

            <h4>Helpers automáticos</h4>
            <p>
              Cada valor enriquecido tiene sufijos para formatearlo. Sin sufijo
              usa el default.
            </p>

            <h5>Fechas</h5>
            <pre>{`[[fechaEmision]]              ← .dma por default
[[fechaEmision.dma]]          → 20/05/2026
[[fechaEmision.amd]]          → 2026-05-20
[[fechaEmision.d]]            → 20
[[fechaEmision.m]]            → 05
[[fechaEmision.a]]            → 2026
[[fechaEmision.mNombre]]      → Mayo
[[fechaEmision.mNombreCorto]] → May
[[fechaEmision.dmaLargo]]     → 20 de mayo de 2026`}</pre>

            <h5>Sitios / direcciones</h5>
            <pre>{`// Sitios de proceso: proceso.sitioFabrica, proceso.sitioFabricante,
// proceso.sitiosAdicionales (array).
// Sede principal del cliente en raíz: sitioPrincipal.
// Todos exponen los mismos campos enriquecidos:

[[sitioX]]              ← .completa por default
[[sitioX.nombre]]       [[sitioX.telefono]]
[[sitioX.direccion]]    [[sitioX.direccion2]]
[[sitioX.codigoPostal]] [[sitioX.ciudad]]
[[sitioX.estado]]       [[sitioX.pais]]
[[sitioX.completa]]     → "Calle 123, Apto 4, 11000 Montevideo, Uruguay"
[[sitioX.cortaSinPais]] → "Calle 123, Montevideo"
[[sitioX.lineaCalle]]   → "Calle 123, Apto 4"
[[sitioX.lineaCiudad]]  → "11000 Montevideo, Uruguay"

// Campos adicionales del sitio (definidos por SitioTipo en config):
[[sitioX.camposPorNombre.<nombreCampo>]]   → valor del campo "nombreCampo"
[[#sitioX.camposAdicionales]][[nombreCampo]]: [[valor]]
[[/sitioX.camposAdicionales]]

// Ejemplos concretos para los sitios del proceso:
[[proceso.sitioFabrica.completa]]
[[proceso.sitioFabricante.nombre]]
[[#proceso.sitiosAdicionales]][[nombre]] — [[completa]]
[[/proceso.sitiosAdicionales]]

// Un sitio que no existe llega igual, con todos los campos vacíos: el bloque
// [[#proceso.sitioFabrica]] se muestra SIEMPRE. Para mostrar algo solo si el
// sitio existe, condicioná con .completa y usá el path completo adentro:
[[#proceso.sitioFabrica.completa]]Fábrica: [[proceso.sitioFabrica.nombre]] — [[proceso.sitioFabrica.completa]]
[[/proceso.sitioFabrica.completa]]

// En certificado, marca, etiquetas y documento de actividad, sitioPrincipal
// no trae nombre ni teléfono: esos dos tags salen vacíos.`}</pre>

            <h5>Números y monedas</h5>
            <pre>{`[[precio]]              → 1234.5 (crudo)
[[precio.formateado]]   → 1.234,50
[[precio.moneda]]       → $ 1.234,50
[[precio.entero]]       → 1.234
[[precio.porcentaje]]   → 1.234,50%`}</pre>

            <h5>Personas</h5>
            <pre>{`[[emisor.nombre]]
[[emisor.apellido]]
[[emisor.email]]
[[emisor.nombreCompleto]]   → "Federico Donner"
[[emisor.iniciales]]        → "F. D."`}</pre>

            <h5>Booleanos</h5>
            <pre>{`[[norma.acreditada.siNo]]   → "Sí" o "No"
[[norma.acreditada.x]]      → "X" o "" (para checkboxes)
[[norma.acreditada.bool]]   → true o false (solo para condicionales)`}</pre>

            <h5>Acreditaciones</h5>
            <p>
              La norma viaja con sus acreditaciones vigentes. Una acreditación
              suspendida no viaja, igual que si la norma nunca hubiera estado
              acreditada. Aplica a Certificado, Marca de Conformidad y
              Documento de actividad (en la raíz, como <code>norma</code>) y a
              la Propuesta Comercial (como <code>certificado.norma</code>).
            </p>
            <pre>{`[[#norma.acreditacion]][[nombre]] ([[pais]]) [[linkAlcance]]
[[/norma.acreditacion]]
   (primera acreditación vigente; no muestra nada sin acreditación)

[[#norma.acreditaciones]][[nombre]] ([[pais]]) [[linkAlcance]]
[[/norma.acreditaciones]]
   (todas las vigentes)

Además hay un tag por acreditación vigente, armado con el nombre del
acreditador sin espacios ni acentos, ej. para "OUA":
[[acreditacionOUA.x]]       → "X" si la norma está acreditada por OUA
[[acreditacionOUA.siNo]]    → "Sí" (si no está acreditada, vacío)
[[#acreditacionOUA.bool]] ... [[/acreditacionOUA.bool]]  (si está acreditada)
[[^acreditacionOUA.bool]] ... [[/acreditacionOUA.bool]]  (si no lo está)
En la propuesta van dentro de la norma del certificado:
[[#certificado.norma.acreditacionOUA.bool]] ... [[/certificado.norma.acreditacionOUA.bool]]

Marca de acreditación (imagen): insertar en el template una imagen
cualquiera (define tamaño y posición) con el tag como TEXTO ALTERNATIVO,
ej. [[acreditacionOUA]]. Al generar el PDF se reemplaza por la marca de
esa acreditación si la norma está acreditada; si no, queda invisible.
- Funciona solo si el organismo tiene cargada esa acreditación con su
  marca; si no, la imagen del template queda tal cual.
- Se reemplaza UNA imagen con ese texto alternativo por parte del
  documento (cuerpo, cada encabezado, cada pie). Dentro de un loop sí se
  reemplazan todas las repeticiones.
- En la propuesta envolvé la imagen en el condicional de la norma del
  certificado ([[#certificado.norma.acreditacionOUA.bool]]).
- La Etiqueta de Datos no tiene acreditaciones: la marca queda invisible.

[[norma.acreditada]] sale del flag de la norma y puede decir "Sí" aunque
la acreditación entera esté suspendida. Para saber si hay alguna vigente,
usá [[#norma.acreditacion]].`}</pre>

            <h4>Loops (repetidores)</h4>
            <p>
              Sirven para iterar arrays (productos, actividades, etc.). La
              apertura <code>[[#nombre]]</code> y el cierre{" "}
              <code>[[/nombre]]</code> envuelven el contenido a repetir.
            </p>
            <p>
              <b>Convención del proyecto para loops verticales</b>: abrir el tag{" "}
              <i>pegado</i> al contenido en la primera línea y cerrar en una
              línea aparte. Es lo que funciona de manera más consistente con
              docxtemplater en .docx producidos en Word:
            </p>
            <pre>{`[[#producto.campospropios]][[nombre]]: [[valor]]
[[/producto.campospropios]]`}</pre>
            <p>
              Si en lugar de eso ponés la apertura sola en una línea, Word
              suele dejar líneas en blanco entre items.
            </p>
            <p>
              <b>Loops en tablas</b>: la apertura va en la primera celda de la
              fila a repetir y el cierre en la última celda de la misma fila.
              Word duplica la fila por cada item, no la tabla entera.
            </p>
            <pre>{`| Concepto                              | Precio                                  |
| [[#actividades]][[actividad.concepto]] | [[actividad.precio.moneda]][[/actividades]] |`}</pre>

            <h4>Condicionales</h4>
            <p>
              Usan la misma sintaxis que los loops, pero con un valor que no es
              array:
            </p>
            <dl>
              <dt>[[#campo]] ... [[/campo]]</dt>
              <dd>
                Muestra el contenido solo si <code>campo</code> es truthy
                (objeto, número &gt; 0, string no vacío). Si dentro hay tags,
                se evalúan contra <code>campo</code> como contexto.
              </dd>
              <dt>[[^campo]] ... [[/campo]]</dt>
              <dd>
                Sección inversa. Muestra el contenido solo si{" "}
                <code>campo</code> es null, undefined, false o string vacío.
              </dd>
            </dl>
            <p>
              <b>Para booleanos:</b> usar <code>.bool</code> obligatoriamente,
              porque el helper de booleano es un objeto enriquecido y siempre
              sería truthy:
            </p>
            <pre>{`[[#norma.acreditada.bool]]
   Texto si está acreditada
[[/norma.acreditada.bool]]

[[^norma.acreditada.bool]]
   Texto si no está acreditada
[[/norma.acreditada.bool]]`}</pre>

            <h4>Globales (todos los templates)</h4>
            <p>Fecha del momento de generación del PDF:</p>
            <pre>{`[[fechaDMA]]     [[fechaD]]     [[fechaM]]
[[fechaA]]       [[fechaMNombre]]`}</pre>

            <h4>Estructura de tags por tipo</h4>
            <p>
              Listado completo con la jerarquía indentada. Las variantes de
              formato (fechas <code>.dma</code>, sitios <code>.completa</code>,
              números <code>.moneda</code>, booleanos <code>.siNo</code>/
              <code>.bool</code>, personas <code>.nombreCompleto</code>) se
              omiten para no inflar el listado — mirá la sección{" "}
              <i>Helpers automáticos</i> arriba para las variantes disponibles
              en cada tipo de dato.
            </p>

            <h5>Globales (todos los tipos)</h5>
            <pre>{`[[fechaDMA]]              20/05/2026 (fecha actual)
[[fechaD]]                20
[[fechaM]]                05
[[fechaA]]                2026
[[fechaMNombre]]          Mayo`}</pre>

            <h5>Etiqueta de Ensayo</h5>
            <pre>{`[[numeroEnsayo]]          1000
[[numeroMuestra]]         12 (vacío si el ensayo no tiene muestra)
[[claveSeguridad]]        clave corta para la página pública
[[notasEtiqueta]]         notas cargadas al registrar el ensayo
[[urlVerificacion]]       https://.../E/1000`}</pre>
            <p>
              <b>El QR no es un tag de texto.</b> Insertá una imagen cualquiera
              (PNG) donde va el QR, con el tamaño y la posición finales, y
              ponele <code>[[qr]]</code> como <i>texto alternativo</i> (clic
              derecho → Ver texto alternativo). Al generar la etiqueta, esa
              imagen se reemplaza por el QR de{" "}
              <code>[[urlVerificacion]]</code> conservando su tamaño y
              posición. La clave de seguridad se imprime pero nunca se
              codifica en el QR. El tamaño de página del documento define el
              tamaño de la etiqueta.
            </p>
            <p>
              <b>Un template Word sirve solo para la etiqueta suelta.</b> La
              impresión de etiquetas en lote exige un template script.
            </p>

            <h5>Etiqueta de Muestra, Etiqueta de Caja y Certificado de familias de juguetes</h5>
            <p>
              Solo admiten template script: ver sus datos en las instrucciones
              del template script.
            </p>

            <h5>Marca de Conformidad</h5>
            <pre>{`[[certificado.id]]
[[certificado.numero]]                      ej "CP-J-1002"

[[proceso.nombre]]
[[proceso.nombrefantasia]]
[[procesotipo.letra]]

[[certificadotipo.codigo]]
[[certificadotipo.nombre]]
[[certificadotipo.reglamentacion]]

[[cliente.razonsocial]]                     (del cliente extendido si es una
[[cliente.nombrefantasia]]                   extensión descargada por el cliente)

[[norma.nombrecertificado]]
[[norma.numero]]
[[norma.acreditada]]                        (helper booleano)
   [[norma.acreditada.siNo]]
   [[norma.acreditada.x]]
   [[norma.acreditada.bool]]               (para condicionales)
[[#norma.acreditacion]]...[[/norma.acreditacion]]       (ver Acreditaciones)
[[#norma.acreditaciones]]...[[/norma.acreditaciones]]
[[acreditacion<Nombre>]]                    (tag por acreditación vigente, en raíz)

[[fechaEmision]]                            (helper fecha)
   [[fechaEmision.dma]], .amd, .d, .m, .a, .mNombre, .mNombreCorto, .dmaLargo
[[fechaVencimiento]]
   (mismas variantes)

[[sitioPrincipal]]                          (helper sitio; sin nombre ni teléfono)
   [[sitioPrincipal.direccion]], .direccion2, .codigoPostal
   [[sitioPrincipal.ciudad]], .estado, .pais
   [[sitioPrincipal.completa]]
   [[sitioPrincipal.cortaSinPais]]
   [[sitioPrincipal.lineaCalle]]
   [[sitioPrincipal.lineaCiudad]]

// Sitios del proceso: van ANIDADOS bajo proceso (todas las variantes de sitio).
[[proceso.sitioFabrica.completa]]
[[proceso.sitioFabricante.completa]]
[[#proceso.sitioFabrica.completa]]          (bloque: solo si existe el sitio)
   [[proceso.sitioFabrica.nombre]], [[proceso.sitioFabrica.completa]]
[[/proceso.sitioFabrica.completa]]
[[#proceso.sitiosAdicionales]][[nombre]] — [[completa]]
[[/proceso.sitiosAdicionales]]              (loop de sitios adicionales)`}</pre>

            <h5>Etiqueta de Datos del Cliente</h5>
            <pre>{`[[certificado.numero]]                      ej "CP-J-1002"

[[cliente.razonsocial]]                     (del cliente extendido si es una
                                             extensión descargada por el cliente)

[[proceso.nombre]]
[[proceso.norma.numero]]
[[procesotipo.letra]]

[[certificadotipo.codigo]]

[[sitioPrincipal]]                          (helper sitio; sin nombre ni teléfono)
   [[sitioPrincipal.direccion]], .direccion2, .codigoPostal
   [[sitioPrincipal.ciudad]], .estado, .pais
   [[sitioPrincipal.completa]]
   [[sitioPrincipal.cortaSinPais]]
   [[sitioPrincipal.lineaCalle]]
   [[sitioPrincipal.lineaCiudad]]

// Sitios del proceso: anidados bajo proceso (ver Marca de Conformidad):
[[proceso.sitioFabrica.completa]]
[[#proceso.sitioFabrica.completa]]
   [[proceso.sitioFabrica.nombre]], [[proceso.sitioFabrica.telefono]]
[[/proceso.sitioFabrica.completa]]
[[proceso.sitioFabricante.completa]]
[[#proceso.sitiosAdicionales]][[nombre]] — [[completa]]
[[/proceso.sitiosAdicionales]]

// Sin acreditaciones.`}</pre>

            <h5>Etiqueta de Producto</h5>
            <pre>{`[[proceso.nombre]]
[[procesotipo.letra]]

[[certificadotipo.codigo]]
[[certificadotipo.nombre]]

[[cliente.razonsocial]]                     (siempre el titular del proceso,
[[cliente.nombrefantasia]]                   también en extensiones)
[[razonSocialCliente]]                      (razón social del cliente
                                            extendido si aplica, sino la
                                            del cliente raíz)
[[extension.numero]]                        (nro de extensión; vacío si no aplica)
[[#extension]]/[[numero]][[/extension]]     (sección: "/N" solo si es extensión)

[[producto.id]]

// Campos propios del producto (loop):
[[#producto.campospropios]][[etiqueta]]: [[valor]]
[[/producto.campospropios]]

   // Dentro del loop:
   [[etiqueta]]          // nombre visible del campo, ej. "Marca"
   [[nombre]]            // código para templates del campo (puede estar vacío)
   [[valor]]             // ej. "Aiwa"
   [[#enviaraurseabool]]...[[/enviaraurseabool]]   // si el campo se envía a URSEA

// Un campo propio puntual, por su código para templates (sin iterar todos;
// vacío si el producto no tiene ese campo):
[[producto.camposPorNombre.Marca]]          // ej. "Aiwa"

[[sitioPrincipal]]                          (helper sitio; sede del cliente)
   (todas las variantes de sitio)

// Sitios del proceso: anidados bajo proceso (ver Marca de Conformidad).
[[proceso.sitioFabrica.completa]]
[[proceso.sitioFabricante.completa]]
[[#proceso.sitiosAdicionales]][[nombre]] — [[completa]]
[[/proceso.sitiosAdicionales]]`}</pre>

            <h5>Certificado</h5>
            <pre>{`[[certificado.numero]]                      ej "CP-J-1002" (en una extensión, el
                                             del certificado original: sumale
                                             [[#extension]]/[[numero]][[/extension]])

[[proceso.nombre]]
[[proceso.nombrefantasia]]
[[procesotipo.letra]]

[[certificadotipo.codigo]]
[[certificadotipo.nombre]]
[[certificadotipo.reglamentacion]]
[[certificadotipo.textolegalcertificado]]
[[certificadotipo.duracioncertificado]]
[[certificadotipo.nombreursea]]
[[certificadotipo.gestionadoursea]]         (helper booleano)
   [[certificadotipo.gestionadoursea.siNo]]
   [[certificadotipo.gestionadoursea.x]]
   [[certificadotipo.gestionadoursea.bool]]

[[esquema.nombre]]                          (nombre del certificado digital en URSEA)
[[esquema.etiqueta]]                        (nombre visible del esquema)

[[norma.nombrecertificado]]
[[norma.numero]]
[[norma.acreditada]]                        (helper booleano)
   [[norma.acreditada.siNo]]
   [[norma.acreditada.x]]
   [[norma.acreditada.bool]]
[[#norma.acreditacion]][[nombre]] ([[pais]]) [[linkAlcance]]
[[/norma.acreditacion]]                     (ver Acreditaciones)
[[#norma.acreditaciones]][[nombre]] ([[pais]]) [[linkAlcance]]
[[/norma.acreditaciones]]
[[acreditacion<Nombre>]]                    (tag por acreditación vigente, en raíz)

[[cliente.razonsocial]]
[[cliente.nombrefantasia]]

[[extension.numero]]                        (nro de extensión; vacío si no aplica)
[[#extension]]/[[numero]][[/extension]]     (sección: agrega "/N" SOLO si es una
                                             extensión → CP-<proceso>/<numero>.
                                             En un cert normal no renderiza nada)

[[fechaEmision]]                            (helper fecha)
   [[fechaEmision.dma]], .amd, .d, .m, .a, .mNombre, .mNombreCorto, .dmaLargo
[[fechaVencimiento]]
   (mismas variantes)
[[actividad.fechafinalizacion]]
   (mismas variantes)

[[sitioPrincipal]]                          (helper sitio; sin nombre ni teléfono)
   [[sitioPrincipal.direccion]], .direccion2, .codigoPostal
   [[sitioPrincipal.ciudad]], .estado, .pais
   [[sitioPrincipal.completa]]
   [[sitioPrincipal.cortaSinPais]]
   [[sitioPrincipal.lineaCalle]]
   [[sitioPrincipal.lineaCiudad]]

// Sitios del proceso: anidados bajo proceso (todas las variantes de sitio):
[[proceso.sitioFabrica.completa]]
[[proceso.sitioFabricante.completa]]
[[#proceso.sitioFabrica.completa]]          (bloque: solo si existe el sitio)
   [[proceso.sitioFabrica.nombre]], [[proceso.sitioFabrica.completa]]
[[/proceso.sitioFabrica.completa]]
[[#proceso.sitiosAdicionales]][[nombre]] — [[completa]]
[[/proceso.sitiosAdicionales]]              (loop de sitios adicionales)

// Reportes de ensayo (loop sobre array; un certificado puede tener varios):
[[#reportesEnsayo]][[reporteEnsayo.id]] - [[reporteEnsayo.codigo]]
   [[reporteEnsayo.fechaemision]]           (.dma, .amd, etc.)
   [[reporteEnsayo.laboratorio.razonsocial]]
   [[reporteEnsayo.laboratorio]]            (helper sitio del laboratorio)
      [[reporteEnsayo.laboratorio.direccion]], .codigoPostal
      [[reporteEnsayo.laboratorio.ciudad]], .estado, .pais
      [[reporteEnsayo.laboratorio.completa]], .cortaSinPais,
      .lineaCalle, .lineaCiudad
[[/reportesEnsayo]]

[[^reportesEnsayo]]
   Sin reportes de ensayo asociados.
[[/reportesEnsayo]]

// Productos del certificado (loop sobre el array producto):
[[#producto]]Producto [[id]]
   // Campos propios del producto (loop interno):
   [[#campospropios]][[etiqueta]]: [[valor]]
   [[/campospropios]]
      // Dentro del loop: [[etiqueta]] (nombre visible), [[nombre]]
      // (código para templates), [[valor]],
      // [[#enviaraurseabool]]...[[/enviaraurseabool]]

   // Un campo propio puntual, por su código para templates (sin iterar
   // todos; vacío si el producto no tiene ese campo). El código va tal
   // cual está definido, con mayúsculas y tildes:
   [[camposPorNombre.Marca]]
   [[camposPorNombre.País de origen]]
[[/producto]]

// Tabla de productos con campos elegidos (ver "Loops en tablas" arriba —
// la fila se repite por cada producto):
| [[#producto]][[camposPorNombre.Marca]] | [[camposPorNombre.Modelo]][[/producto]] |`}</pre>

            <h5>Documento de actividad (al finalizar)</h5>
            <pre>{`// Se genera automáticamente al finalizar una actividad cuyo tipo tiene un
// template configurado, y queda en los archivos de la actividad visible
// para el cliente.
//
// Incluye TODOS los tags de "Certificado" (proceso, procesotipo,
// certificadotipo, esquema, norma con acreditaciones, cliente, sitios,
// reportes de ensayo, productos) EXCEPTO extension, fechaEmision y
// fechaVencimiento, que acá no aplican (quedan vacíos). Además agrega:

[[fechaGeneracion]]                         (helper fecha) momento en que se
   [[fechaGeneracion.dma]], .amd, .d,       genera el documento
   .m, .a, .mNombre, .mNombreCorto, .dmaLargo

[[comentarioFinal]]                         comentario del colaborador en el
                                            último paso de la actividad (si el
                                            último paso lo decidió un admin,
                                            usa el comentario del admin)

[[certificado.numero]]                      número del certificado vigente del
                                            proceso (vacío si no hay). También
                                            como [[certificadoVigente.numero]]
[[cliente.contactos]]                       contactos del cliente, separados por coma

[[actividad.tipo.nombre]]                   nombre del tipo de actividad
[[actividad.fechacreacion]]                 (helper fecha)
[[actividad.fechafinalizacion]]             (helper fecha)

// Pasos de la actividad (loop, ordenados por el orden de configuración):
[[#actividad.pasos]][[nombre]] (paso [[orden]])
   [[fechaInicio]]                          (helper fecha) inicio del paso
   [[fechaDecision]]                        (helper fecha) última decisión
   [[aprobado]]                             (helper booleano)
      [[aprobado.siNo]], .x, .bool
   [[comentarios]]                          comentarios de la decisión

   // Colaboradores asignados al paso (loop, solo asignaciones activas):
   [[#colaboradores]][[nombreCompleto]] ([[iniciales]])
      [[nombre]], [[apellido]], [[email]]
      [[decisor]]                           (helper booleano) → .siNo, .x, .bool
      [[enformacion]]                       (helper booleano)
   [[/colaboradores]]
[[/actividad.pasos]]

// Todos los colaboradores de la actividad, sin repetir (para firmas):
[[#actividad.colaboradores]][[nombreCompleto]] — [[email]]
[[/actividad.colaboradores]]
   // Dentro del loop: [[nombre]], [[apellido]], [[email]],
   // [[nombreCompleto]], [[iniciales]]`}</pre>

            <h5>Propuesta Comercial</h5>
            <pre>{`[[propuesta.id]]
[[propuesta.notas]]
[[propuesta.textoImportante]]               texto "importante" de la configuración (texto plano)
[[propuesta.textoAceptacion]]               texto de aceptación de la configuración (texto plano)
[[@propuesta.textoImportanteFormato]]       lo mismo CON formato (negrita, cursiva, rojo, subrayado,
[[@propuesta.textoAceptacionFormato]]       viñetas, sangría). Va SOLO en su párrafo, con la @. Hereda
                                            la fuente, tamaño, color y alineación del tag en el
                                            template; las etiquetas solo suman negrita, cursiva,
                                            subrayado y rojo.
                                            Los cuatro se congelan al enviar la propuesta al cliente.
[[propuesta.masiva]]                        (helper booleano)
   [[propuesta.masiva.siNo]]                "Sí" si es "más IVA"
   [[propuesta.masiva.x]]
   [[propuesta.masiva.bool]]
[[propuesta.fechaemision]]                  (helper fecha)
   [[propuesta.fechaemision.dma]], .amd, .d, .m, .a, .mNombre, .mNombreCorto, .dmaLargo
[[propuesta.fechaaceptacion]]               (puede ser null)
   (mismas variantes de fecha)

[[#propuesta.fechaaceptacion]]
   Aceptada el [[propuesta.fechaaceptacion.dma]]
[[/propuesta.fechaaceptacion]]

[[cliente.razonsocial]]
[[cliente.nombrefantasia]]
[[cliente.rut]]

[[sitioPrincipal]]                          (helper sitio)
   (todas las variantes de sitio)

[[#sitioFabrica.completa]]                  (solo si hay fábrica)
   [[sitioFabrica.nombre]] — [[sitioFabrica.completa]]
[[/sitioFabrica.completa]]

[[#sitioFabricante.completa]]
   [[sitioFabricante.completa]]
[[/sitioFabricante.completa]]

[[emisor]]                                  (helper persona)
   [[emisor.nombre]]
   [[emisor.apellido]]
   [[emisor.email]]
   [[emisor.nombreCompleto]]
   [[emisor.iniciales]]

[[#aceptador]]                              (puede ser null)
   [[aceptador.nombreCompleto]]             nombre y apellido, como texto
   [[aceptador.nombre]]                     igual que nombreCompleto
   [[aceptador.iniciales]]
[[/aceptador]]

[[#solicitud]]                              (puede ser null)
   [[solicitud.id]]
   [[solicitud.nombre]]                     nombre de la solicitud
   [[solicitud.sitioFabrica.completa]]      (todas las variantes de sitio)
   [[solicitud.sitioFabricante.completa]]
[[/solicitud]]

// El certificado de la propuesta: lo que es del tipo de certificado y no de
// cada actividad. Si la propuesta cubre más de un tipo, es el primero (todos
// están en gruposPorCertificado, con los mismos campos).
[[certificado.titulo]]                      "<tipo de proceso> - <tipo de certificado>"
[[certificado.nombre]]                      nombre del tipo de certificado
[[certificado.procesotipo]]                 nombre del tipo de proceso
[[certificado.notas]]                       notas de la propuesta (del tipo de certificado)
[[certificado.reglamentacion]]              reglamentación del tipo de certificado

// Norma de los procesos de la propuesta:
[[certificado.norma]]                       "número - nombre"
[[certificado.norma.numero]]
[[certificado.norma.nombre]]
[[certificado.norma.acreditada]]            (helper booleano: tiene alguna acreditación vigente)
[[#certificado.norma.acreditaciones]][[nombre]] ([[pais]]) [[linkAlcance]]
[[/certificado.norma.acreditaciones]]       organismo, país y URL del alcance
[[#certificado.norma.acreditacion]]...[[/certificado.norma.acreditacion]]  (solo la primera)
// Marca: imagen con texto alternativo [[acreditacionOUA]] envuelta en
[[#certificado.norma.acreditacionOUA.bool]] (imagen) [[/certificado.norma.acreditacionOUA.bool]]

// Esquema de los procesos de la propuesta:
[[certificado.esquema.nombre]]              nombre visible del esquema (la etiqueta)
[[certificado.esquema.descripcion]]
[[certificado.esquema.detalles]]
[[#certificado.esquema.nombre]]...[[/certificado.esquema.nombre]]   (solo si hay esquema)

// Si los procesos de la propuesta tienen normas o esquemas distintos,
// certificado.norma y certificado.esquema son los del primero. Para
// listarlos todos (mismos campos):
[[#certificado.normas]][[numero]] - [[nombre]][[/certificado.normas]]
[[#certificado.esquemas]][[nombre]]: [[descripcion]][[/certificado.esquemas]]

// Total general (suma de todos los totales):
[[precios.total]]                           (helper número)
   [[precios.total.moneda]], .formateado, .entero

// Totales por moneda (loop):
[[#precios.totales]][[moneda.nombre]] ([[moneda.simbolo]]): [[total.moneda]]
[[/precios.totales]]
   // Dentro del loop de totales:
   [[total.formateado]], .moneda, .entero
   [[moneda.simbolo]], .nombre

// OPCIÓN A — actividades AGRUPADAS por tipo de certificado (recomendado, igual
// que se ve en la plataforma). Loop externo por certificado, interno por actividad:
[[#gruposPorCertificado]]
   [[certificado.titulo]]                   "<tipo de proceso> - <tipo de certificado>"
   [[certificado.nombre]]                   nombre del tipo de certificado
   [[certificado.procesotipo]]              nombre del tipo de proceso
   [[certificado.notas]]                    notas de la propuesta (del tipo de certificado)
   [[certificado.reglamentacion]]           (y todos los campos de certificado de
   [[certificado.norma.numero]]              arriba: norma, esquema, normas, esquemas)
   [[#actividades]]
      [[actividad.actividadnombre]]         "Certificación"
      [[actividad.frecuencia]]              "Anual" | ... | "Por actividad"
      [[actividad.cantidad.entero]]         cantidad de la primera facturación
      [[actividad.preciounitario.moneda]]   precio unitario con símbolo
      [[actividad.subtotal.moneda]]         subtotal (cantidad × precio)
   [[/actividades]]
[[/gruposPorCertificado]]

// OPCIÓN B — actividades en una tabla plana. Tags de apertura/cierre DENTRO de
// las celdas de la fila a repetir:
| Actividad                                     | Facturación               | P. Unitario                         | Subtotal                                       |
| [[#actividades]][[actividad.actividadnombre]] | [[actividad.frecuencia]] | [[actividad.preciounitario.moneda]] | [[actividad.subtotal.moneda]][[/actividades]] |

   // Campos disponibles dentro del loop de actividades (en ambas opciones):
   [[actividad.actividadnombre]]            "Certificación"
   [[actividad.certificado]]                nombre del tipo de certificado
   [[actividad.concepto]]                   = nombre del tipo de certificado
   [[actividad.notas]]                      notas del tipo de certificado
   [[actividad.frecuencia]]                 "Anual" | ... | "Por actividad"
   [[actividad.cantidad]]                   (helper número) → .entero, .formateado
   [[actividad.moneda.simbolo]], .nombre
   [[actividad.precio]]                     (helper número) precio unitario
   [[actividad.precio.formateado]], .moneda, .entero
   [[actividad.preciounitario]]             (alias de .precio)
   [[actividad.subtotal]]                   (precio × cantidad)
   [[actividad.subtotal.formateado]], .moneda, .entero

   [[#actividad.descuento]]                 (condicional; null si no aplica)
      [[actividad.descuento.porcentaje.porcentaje]]   "10,00%"
      [[actividad.descuento.concepto]]
   [[/actividad.descuento]]

// Adicionales (ítems sueltos; loop):
[[#adicionales]]
   [[adicional.descripcion]]
   [[adicional.precio.moneda]]              (helper número con símbolo)
   [[adicional.moneda.simbolo]], .nombre
[[/adicionales]]

// Procesos alcanzados por la propuesta
[[#procesos]]
   [[proceso.nombre]] - [[proceso.nombrefantasia]]
[[/procesos]]`}</pre>

            <h4>Tips finales</h4>
            <ul>
              <li>
                <b>Tags no resueltos quedan vacíos</b> — si ves espacios donde
                debería haber datos, abrí los logs del backend: cada render
                imprime <code>tags no resueltos: ...</code> con la lista exacta.
              </li>
              <li>
                <b>Booleanos siempre con <code>.bool</code></b> en condicionales,
                nunca el tag pelado.
              </li>
              <li>
                <b>Sitios en condicionales con <code>.completa</code></b>: un
                sitio que no existe llega igual (vacío), así que{" "}
                <code>[[#sitioX]]</code> se muestra siempre.
              </li>
              <li>
                <b>Loops verticales</b> con el patrón "apertura pegada al
                contenido + cierre en línea aparte" para evitar líneas en blanco.
              </li>
              <li>
                <b>Tablas</b>: tags de apertura y cierre dentro de la fila a
                repetir, no fuera de la tabla.
              </li>
            </ul>
          </Dialog>
        </div>
      )}
    </>
  );
}
